/**
 * MONSTER TOWN — THE LOOP. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * Fixed-timestep simulation with interpolated render. 30 Hz sim, uncapped
 * render. 30 Hz is deliberate: on an older iPad the render is the expensive
 * half, and nothing in this game needs 60 Hz logic. Systems get a constant
 * `dt` so behaviour is identical on a fast desktop and a slow hand-me-down.
 *
 * HOW A CREW PLUGS IN:
 *   engine.addSystem({ id: 'npc', order: 40, fixed: (dt) => {...}, render: (a) => {...} })
 *
 * ORDER BANDS (keep out of other crews' bands):
 *    0- 9  input / camera intent
 *   10-19  save + persistence
 *   20-29  build system / placement
 *   30-39  player monster
 *   40-49  npcs / town life
 *   50-59  venue / economy simulation
 *   60-69  interactions & prop animation
 *   70-79  particles / vfx
 *   80-89  audio
 *   90-99  hud / ui
 *
 * `fixed(dt)` runs 0..MAX_STEPS times per frame with dt = 1/30 exactly.
 * `render(alpha)` runs once per frame; `alpha` is 0..1 between sim steps — use
 * it to interpolate positions so movement is smooth at any frame rate.
 */

import { bus } from './events'
import type { FrameStats } from './types'

export const SIM_HZ = 30
export const SIM_DT = 1 / SIM_HZ
/** Never simulate more than this many steps in one frame (death-spiral guard). */
const MAX_STEPS = 4
/** A frame gap bigger than this (tab backgrounded) is dropped, not caught up. */
const MAX_FRAME_MS = 250

export interface System {
  readonly id: string
  readonly order: number
  /** Constant-dt simulation. Runs 0..4 times per frame. */
  readonly fixed?: (dt: number) => void
  /** Once per frame. `alpha` interpolates between the last two sim states. */
  readonly render?: (alpha: number, frameMs: number) => void
  /** Paused systems keep their state but stop ticking (e.g. NPCs in a menu). */
  paused?: boolean
}

export interface PerfSample {
  frames: number
  p50: number
  p95: number
  p99: number
  max: number
  longFrames: number
  avgFps: number
  /**
   * CPU milliseconds actually spent inside the sim + render systems, i.e. the
   * work WE do. Frame interval alone is a lie in a headless browser (and on an
   * iPad in low-power mode) because it is clamped to the display cadence — a
   * game doing 3ms of work and one doing 30ms both report 33.3ms there. Gate on
   * these numbers, report the interval ones.
   */
  workP50: number
  workP95: number
  workP99: number
  workMax: number
  /** Median frame interval — the observed display cadence. */
  cadenceMs: number
  /** Frames longer than 1.75x the cadence: real, visible hitches. */
  hitches: number
}

export class Engine {
  private systems: System[] = []
  private raf = 0
  private last = 0
  private acc = 0
  private running = false
  private frames: number[] = []
  private work: number[] = []
  private simSteps = 0

  /** Wall-clock ms since start(). Deterministic under harness stepping. */
  now = 0

  /** Live per-frame numbers for the HUD/perf harness. Renderer crew fills these. */
  stats: FrameStats = {
    fps: 0,
    frameMs: 0,
    workMs: 0,
    drawCalls: 0,
    triangles: 0,
    programs: 0,
    renderScale: 1,
    pixelRatio: 1,
  }

  /** Seeded RNG shared by everything that must be reproducible in tests. */
  private seedState = 0x9e3779b9
  seed(n: number): void {
    this.seedState = n >>> 0 || 1
  }
  rng = (): number => {
    this.seedState = (Math.imul(this.seedState, 1664525) + 1013904223) >>> 0
    return this.seedState / 0x100000000
  }

  addSystem(s: System): () => void {
    this.systems.push(s)
    this.systems.sort((a, b) => a.order - b.order)
    return () => this.removeSystem(s.id)
  }

  removeSystem(id: string): void {
    const i = this.systems.findIndex((s) => s.id === id)
    if (i >= 0) this.systems.splice(i, 1)
  }

  system(id: string): System | undefined {
    return this.systems.find((s) => s.id === id)
  }

  systemIds(): string[] {
    return this.systems.map((s) => s.id)
  }

  setPaused(id: string, paused: boolean): void {
    const s = this.system(id)
    if (s !== undefined) s.paused = paused
  }

  start(): void {
    if (this.running) return
    this.running = true
    this.last = performance.now()
    bus.emit('app:booted', { ms: Math.round(this.last) })
    const loop = (t: number): void => {
      this.raf = requestAnimationFrame(loop)
      this.tick(t)
    }
    this.raf = requestAnimationFrame(loop)
  }

  stop(): void {
    this.running = false
    if (this.raf !== 0) cancelAnimationFrame(this.raf)
    this.raf = 0
  }

  private tick(t: number): void {
    let frameMs = t - this.last
    this.last = t
    if (frameMs > MAX_FRAME_MS) frameMs = SIM_DT * 1000
    this.now += frameMs
    this.stats.frameMs = frameMs
    this.stats.fps = frameMs > 0 ? 1000 / frameMs : 0

    this.frames.push(frameMs)
    if (this.frames.length > 2400) this.frames.shift()

    const workStart = performance.now()
    this.acc += frameMs / 1000
    let steps = 0
    while (this.acc >= SIM_DT && steps < MAX_STEPS) {
      for (const s of this.systems) {
        if (s.paused === true || s.fixed === undefined) continue
        try {
          s.fixed(SIM_DT)
        } catch (err) {
          console.error(`[engine] system "${s.id}" fixed() threw`, err)
        }
      }
      this.acc -= SIM_DT
      steps++
      this.simSteps++
    }
    if (steps === MAX_STEPS) this.acc = 0

    const alpha = this.acc / SIM_DT
    for (const s of this.systems) {
      if (s.paused === true || s.render === undefined) continue
      try {
        s.render(alpha, frameMs)
      } catch (err) {
        console.error(`[engine] system "${s.id}" render() threw`, err)
      }
    }

    const workMs = performance.now() - workStart
    this.work.push(workMs)
    if (this.work.length > 2400) this.work.shift()
    // Smoothed so one expensive frame (a scene build) cannot trip auto-tuning.
    this.stats.workMs = this.stats.workMs * 0.9 + workMs * 0.1
  }

  /** Advance the loop by hand — harness/tests only, never in game code. */
  stepForTest(ms: number): void {
    this.tick(this.last + ms)
  }

  get totalSimSteps(): number {
    return this.simSteps
  }

  resetPerf(): void {
    this.frames.length = 0
    this.work.length = 0
  }

  /** Frame-time and CPU-work percentiles over the recorded window. */
  perf(longFrameMs = 50): PerfSample {
    const f = [...this.frames].sort((a, b) => a - b)
    const w = [...this.work].sort((a, b) => a - b)
    const n = f.length
    if (n === 0) {
      return {
        frames: 0,
        p50: 0,
        p95: 0,
        p99: 0,
        max: 0,
        longFrames: 0,
        avgFps: 0,
        workP50: 0,
        workP95: 0,
        workP99: 0,
        workMax: 0,
        cadenceMs: 0,
        hitches: 0,
      }
    }
    const pick = (arr: number[], q: number): number =>
      arr.length === 0 ? 0 : (arr[Math.min(arr.length - 1, Math.floor(q * arr.length))] ?? 0)
    const sum = f.reduce((a, b) => a + b, 0)
    const cadence = pick(f, 0.5)
    return {
      frames: n,
      p50: cadence,
      p95: pick(f, 0.95),
      p99: pick(f, 0.99),
      max: f[n - 1] ?? 0,
      longFrames: f.filter((x) => x > longFrameMs).length,
      avgFps: 1000 / (sum / n),
      workP50: pick(w, 0.5),
      workP95: pick(w, 0.95),
      workP99: pick(w, 0.99),
      workMax: w[w.length - 1] ?? 0,
      cadenceMs: cadence,
      hitches: f.filter((x) => x > cadence * 1.75).length,
    }
  }
}

export const engine = new Engine()
